/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2022 (original work) Open Assessment Technologies SA ;
 */
import router from '@/core/router';
import { isEmptyValue } from './filters/filters';
import { log } from './logger.js';

/**
 * Gets query param value
 *
 * @param {string} param - param name
 * @returns {String|undefined}
 */
export const getQueryParam = param => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(param);
};

/**
 * Gets all query params
 *
 * @returns { [key: string]: string }
 */
export const getQueryParams = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const params = {};
    urlParams.forEach((value, key) => {
        params[key] = value;
    });
    return params;
};

/**
 * @typedef {Object} OptionObject
 * @property {key} string - key
 * @property {label} string  - label
 */
/**
 * Sets query param value. Serializes the values in the filters to be url friendly so
 * they can be sent to jsreport
 *
 * @param {string} param - param name
 * @param {string | OptionObject | OptionObject[]} value - param value
 */
export const setQueryParam = (param, value) => {
    const searchParams = new URLSearchParams(window.location.search);

    if (!isEmptyValue(value) && typeof value === 'object' && value.key) {
        searchParams.set(param, value.key); // simple select case
    } else if (!isEmptyValue(value) && Array.isArray(value)) {
        const arrayOfKeys = value.map(v => v.key);
        searchParams.set(param, arrayOfKeys); // multi select case, array.toString() will be implicitly used
    } else if (!isEmptyValue(value)) {
        searchParams.set(param, value); // slug or date case (it's also a filter in the url)
    } else {
        log.warn('value in setQueryParam is not an array, a string or an object with a key property', value);
    }

    router.replace(`${window.location.pathname}?${searchParams.toString()}`, false);
};

/**
 * Clear query params values
 *
 */
export const clearQueryParams = () => {
    const newUrl = window.location.pathname;
    router.replace(newUrl, false);
};

/**
 * Sets query params values
 *
 * @param {string} params - param object
 */
export const setQueryParams = params => {
    clearQueryParams();
    Object.keys(params)
        .filter(key => !isEmptyValue(params[key]))
        .forEach(key => setQueryParam(key, params[key]));
};
